import { MathUtils, Vector3 } from 'three';
import { useFrame } from '@react-three/fiber';
import { useLerpedMouse } from '@/hooks/useLerpedMouse';
import { useDeviceOrientation } from '@/hooks/useDeviceOrientation';

export const radiansToDegrees = (rad: number): number => (180 * rad) / Math.PI;

interface UseMouseOrOrientation {
    setOrientationOrigin: () => void;
}

export const useMotionParallax = (
    initialTarget: Vector3,
    initialPosition: Vector3,
    xBoundsForMouse: [number, number], // bounds of x position to move camera with mouse
    yBoundsForMouse: [number, number], // bounds of y position to move camera with mouse
    xBoundsForGyro: [number, number],
    yBoundsForGyro: [number, number]
): UseMouseOrOrientation => {
    const mouse = useLerpedMouse();
    const { orientation, reOrient } = useDeviceOrientation();

    useFrame(({ camera }) => {
        if (orientation.current && orientation.current.beta !== null && orientation.current.gamma !== null) {
            camera.position.set(
                MathUtils.lerp(
                    initialPosition.x + xBoundsForGyro[0],
                    initialPosition.x + xBoundsForGyro[1],
                    orientation.current.gamma / 180
                ),
                MathUtils.lerp(
                    initialPosition.y + yBoundsForGyro[0],
                    initialPosition.y + yBoundsForGyro[1],
                    -orientation.current.beta / 90
                ),
                initialPosition.z
            );
        } else {
            camera.position.set(
                MathUtils.lerp(
                    initialPosition.x + xBoundsForMouse[0],
                    initialPosition.x + xBoundsForMouse[1],
                    mouse.current.x
                ),
                MathUtils.lerp(
                    initialPosition.y + yBoundsForMouse[0],
                    initialPosition.y + yBoundsForMouse[1],
                    mouse.current.y
                ),
                initialPosition.z
            );
        }
        camera.lookAt(initialTarget);
    });

    return {
        setOrientationOrigin: reOrient,
    };
};
